import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { resolveImage } from '../lib/images';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Gallery = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/gallery`);
      setImages(data.images || []);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="min-h-screen pt-20 bg-[#050814]">
      {/* Hero */}
      <section className="relative h-72 flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `linear-gradient(rgba(5, 8, 20, 0.8), rgba(5, 8, 20, 0.9)), url('https://images.unsplash.com/photo-1656740976671-3f4e8bab24c3?auto=format&fit=crop&w=2000&q=80')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="container mx-auto px-4 z-10 text-center text-[#F4F1EA]">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Gallery</h1>
          <p className="text-xl text-[#B8860B]">Explore the beauty of Creek Bottom Ranch &amp; Resort</p>
        </div>
      </section>

      {/* Grid */}
      <section className="py-16 bg-[#050814]">
        <div className="container mx-auto px-4">
          {loading ? (
            <p className="text-center text-[#F4F1EA]/60 py-12">Loading gallery...</p>
          ) : images.length === 0 ? (
            <p className="text-center text-[#F4F1EA]/60 py-12">
              Photos coming soon — check back after our next adventure on the ranch!
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {images.map((image) => (
                <Card
                  key={image.id}
                  data-testid={`gallery-image-${image.id}`}
                  className="group relative overflow-hidden cursor-pointer border border-[#B8860B]/30 shadow-lg hover:shadow-2xl transition-all duration-300"
                  onClick={() => setSelectedImage(image)}
                >
                  <div className="relative aspect-square overflow-hidden bg-[#050814]">
                    <img
                      src={resolveImage(image.url)}
                      alt={image.caption || 'Creek Bottom Ranch'}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-[#050814]/0 group-hover:bg-[#050814]/50 transition-all duration-300 flex items-center justify-center">
                      <span className="text-[#B8860B] opacity-0 group-hover:opacity-100 transition-opacity font-semibold">
                        View
                      </span>
                    </div>
                    {image.caption && (
                      <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-[#050814] to-transparent p-3 opacity-0 group-hover:opacity-100 transition-opacity">
                        <p className="text-sm text-[#F4F1EA]">{image.caption}</p>
                      </div>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {selectedImage && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-50 bg-[#050814]/95 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-4 right-4 text-[#F4F1EA] hover:text-[#B8860B] transition-colors"
            data-testid="gallery-close-btn"
          >
            <X size={32} />
          </button>
          <div className="text-center">
            <img
              src={resolveImage(selectedImage.url)}
              alt={selectedImage.caption || 'Creek Bottom Ranch'}
              className="max-w-full max-h-[80vh] object-contain border border-[#B8860B]/30 mx-auto"
            />
            {selectedImage.caption && (
              <p className="text-[#B8860B] mt-4">{selectedImage.caption}</p>
            )}
          </div>
        </div>
      )}

      {/* CTA */}
      <section className="py-20 bg-[#0B132B] border-t border-[#B8860B]/20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-[#F4F1EA] mb-6">Experience It Yourself</h2>
          <p className="text-lg text-[#F4F1EA]/70 mb-8 max-w-2xl mx-auto">
            These photos only tell part of the story. Book your stay and create your own memories at Creek Bottom.
          </p>
          <Link to="/booking">
            <Button
              data-testid="gallery-book-btn"
              size="lg"
              className="bg-[#B8860B] hover:bg-[#B8860B]/90 text-[#F4F1EA] font-bold px-8 py-6 text-lg transition-all duration-300 hover:scale-105 shadow-lg border-2 border-[#B8860B]"
            >
              Book Your Adventure
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
